import { useState, useEffect } from 'react';
import { Eye, TrendingUp, Calendar, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import apiClient from '@/utils/api';

const PropertyViewsChart = ({ propertyId }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);

  useEffect(() => {
    if (propertyId) {
      fetchStats();
    }
  }, [propertyId, days]);

  const fetchStats = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get(`/properties/${propertyId}/views/stats?days=${days}`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching view stats:', error);
      // Silently fail - stats are not critical
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-[#121212] border border-white/5 rounded-lg p-6">
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 text-[#D4AF37] animate-spin" />
        </div>
      </div>
    );
  }

  if (!stats || stats.total_views === 0) {
    return (
      <div className="bg-[#121212] border border-white/5 rounded-lg p-6">
        <div className="flex items-center gap-2 mb-4">
          <Eye className="w-5 h-5 text-[#D4AF37]" />
          <h3 className="text-white font-medium">Estadísticas de Vistas</h3>
        </div>
        <p className="text-[#A3A3A3] text-sm text-center py-4">
          Sin vistas registradas en los últimos {days} días
        </p>
      </div>
    );
  }

  // Find max views for scaling
  const dailyStats = stats.daily_stats || [];
  const viewsList = dailyStats.map(d => d.views);
  const maxViews = viewsList.length ? Math.max.apply(null, viewsList.concat([1])) : 1;
  const firstDay = dailyStats[0];
  const lastDay = dailyStats[dailyStats.length - 1];

  return (
    <div className="bg-[#121212] border border-white/5 rounded-lg p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <Eye className="w-5 h-5 text-[#D4AF37]" />
          <h3 className="text-white font-medium">Estadísticas de Vistas</h3>
        </div>
        <select
          value={days}
          onChange={(e) => setDays(Number(e.target.value))}
          className="bg-[#0a0a0a] border border-white/10 text-white text-sm rounded px-2 py-1"
          data-testid="views-period-select"
        >
          <option value={7}>7 días</option>
          <option value={14}>14 días</option>
          <option value={30}>30 días</option>
        </select>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="bg-[#0a0a0a] rounded-lg p-4 border border-white/5">
          <div className="flex items-center gap-2 text-[#A3A3A3] text-sm mb-1">
            <Eye className="w-4 h-4" />
            Total vistas
          </div>
          <p className="text-2xl font-bold text-white" data-testid="total-views-count">
            {stats.total_views.toLocaleString()}
          </p>
        </div>
        <div className="bg-[#0a0a0a] rounded-lg p-4 border border-white/5">
          <div className="flex items-center gap-2 text-[#A3A3A3] text-sm mb-1">
            <TrendingUp className="w-4 h-4" />
            Promedio diario
          </div>
          <p className="text-2xl font-bold text-white" data-testid="avg-views-count">
            {stats.avg_daily_views}
          </p>
        </div>
      </div>

      {/* Chart */}
      <div className="mb-2">
        <div className="flex items-end gap-[2px] h-32" data-testid="views-chart">
          {dailyStats.map((day, index) => {
            const height = maxViews > 0 ? (day.views / maxViews) * 100 : 0;
            const isToday = index === dailyStats.length - 1;
            
            return (
              <div
                key={day.date}
                className="flex-1 flex flex-col items-center group relative"
              >
                <div
                  className={`w-full rounded-t transition-colors ${
                    isToday ? 'bg-[#D4AF37]' : 'bg-[#D4AF37]/40 hover:bg-[#D4AF37]/60'
                  }`}
                  style={{ height: `${Math.max(height, 2)}%` }}
                  data-testid={`views-bar-${day.date}`}
                />
                {/* Tooltip */}
                <div className="absolute bottom-full mb-2 hidden group-hover:block z-10">
                  <div className="bg-black border border-white/20 rounded px-2 py-1 text-xs whitespace-nowrap">
                    <p className="text-[#D4AF37] font-medium">{day.views} vistas</p>
                    <p className="text-[#A3A3A3]">
                      {new Date(day.date).toLocaleDateString('es-GT', { 
                        day: 'numeric', 
                        month: 'short' 
                      })}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* X-axis labels */}
      <div className="flex justify-between text-[#525252] text-xs">
        <span>
          {firstDay && new Date(firstDay.date).toLocaleDateString('es-GT', { 
            day: 'numeric', 
            month: 'short' 
          })}
        </span>
        <span>
          {lastDay && new Date(lastDay.date).toLocaleDateString('es-GT', { 
            day: 'numeric', 
            month: 'short' 
          })}
        </span>
      </div>
    </div>
  );
};

export default PropertyViewsChart;
